import { format } from 'date-fns'
import { ProdReleaseSchedule, ReleaseOccurrence, ReleaseWeekday } from '../lib/config/release-config'

const WEEKDAY_INDEX: Record<ReleaseWeekday, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
}

const OCCURRENCE_LABELS: Record<Exclude<ReleaseOccurrence, 'last'>, string> = {
  1: '1st',
  2: '2nd',
  3: '3rd',
  4: '4th',
}

const DAY_IN_MS = 24 * 60 * 60 * 1000

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

const addDays = (date: Date, days: number) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)

const addMonths = (date: Date, months: number) => new Date(date.getFullYear(), date.getMonth() + months, 1)

const getNthWeekdayOfMonth = (year: number, month: number, weekday: number, occurrence: ReleaseOccurrence) => {
  if (occurrence === 'last') {
    const lastDay = new Date(year, month + 1, 0)
    const backwardOffset = (lastDay.getDay() - weekday + 7) % 7
    return new Date(year, month, lastDay.getDate() - backwardOffset)
  }

  const firstDay = new Date(year, month, 1)
  const forwardOffset = (weekday - firstDay.getDay() + 7) % 7
  const dayOfMonth = 1 + forwardOffset + (occurrence - 1) * 7
  const candidate = new Date(year, month, dayOfMonth)

  if (candidate.getMonth() !== month) {
    throw new Error('Release occurrence does not exist in this month')
  }

  return candidate
}

export const getReleaseScheduleLabel = (schedule: ProdReleaseSchedule) => {
  const weekdayLabel = `${schedule.weekday.charAt(0).toUpperCase()}${schedule.weekday.slice(1)}`

  if (schedule.cadence === 'weekly') {
    return schedule.interval === 1
      ? `Every ${weekdayLabel}`
      : `Every ${schedule.interval} weeks on ${weekdayLabel}`
  }

  const occurrenceLabel = schedule.occurrence === 'last' ? 'Last' : OCCURRENCE_LABELS[schedule.occurrence]
  const cadenceLabel = schedule.interval === 1
    ? schedule.cadence
    : `${schedule.interval} ${schedule.cadence === 'monthly' ? 'months' : 'quarters'}`

  return `${occurrenceLabel} ${weekdayLabel} ${cadenceLabel}`
}

export const getNextProdReleaseDate = (schedule: ProdReleaseSchedule, fromDate = new Date()) => {
  const today = startOfDay(fromDate)
  const effectiveFrom = startOfDay(new Date(schedule.effectiveFrom))
  const searchFrom = today > effectiveFrom ? today : effectiveFrom

  if (schedule.cadence === 'weekly') {
    const targetWeekday = WEEKDAY_INDEX[schedule.weekday]
    const anchorWeekStart = addDays(effectiveFrom, -((effectiveFrom.getDay() + 6) % 7))
    let candidate = addDays(anchorWeekStart, (targetWeekday + 6) % 7)

    while (candidate < effectiveFrom) {
      candidate = addDays(candidate, schedule.interval * 7)
    }

    while (candidate < searchFrom) {
      candidate = addDays(candidate, schedule.interval * 7)
    }

    return candidate
  }

  const monthStep = schedule.cadence === 'monthly' ? schedule.interval : schedule.interval * 3
  const anchorMonth = new Date(effectiveFrom.getFullYear(), effectiveFrom.getMonth(), 1)

  for (let monthOffset = 0; monthOffset < 240; monthOffset += monthStep) {
    const monthDate = addMonths(anchorMonth, monthOffset)

    try {
      const candidate = getNthWeekdayOfMonth(
        monthDate.getFullYear(),
        monthDate.getMonth(),
        WEEKDAY_INDEX[schedule.weekday],
        schedule.occurrence
      )

      if (candidate < effectiveFrom) {
        continue
      }

      if (candidate >= searchFrom) {
        return candidate
      }
    } catch {
      continue
    }
  }

  throw new Error('Unable to determine the next production release date from the current schedule')
}

export const getDaysUntilDate = (targetDate: Date, fromDate = new Date()) => {
  const diffMs = startOfDay(targetDate).getTime() - startOfDay(fromDate).getTime()
  return Math.max(0, Math.round(diffMs / DAY_IN_MS))
}

export const formatReleaseDate = (date: Date) => format(date, 'EEE, MMM d')
